import {Box, Button, HStack, Icon, Image, Modal, Pressable, Spinner, Toast, VStack} from "native-base";
import React, {useEffect, useRef, useState} from "react";
import {
    FlatList,
    Platform,
    StyleSheet,
    Text,
    TextInput,
    TouchableWithoutFeedback,
    useWindowDimensions,
    View
} from "react-native";
import apps, {Happyhour, SaleMode} from "./lib/apps";
import {money, holidaySurchargeAmount, payableAmount} from "./lib/utils";
import ThumbImage from "./component/ThumbImage";
import {useScrollMore, ScrollMoreHint} from "./component/ScrollMore";
import {BackButton, CIRCLE_BTN} from "./component/BackButton";
import {
    CateMenuAndOptionData,
    mapToItems,
    PayMethod,
    Receipt,
    ReceiptRes,
    ShopTblData,
    tagOptions,
    YourOrder
} from "./lib/model";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {paymentActiveState, lastCartTouchState} from "./state/idleState";
import {cartState} from "./state/cartState";
import DashedLine from "react-native-dashed-line";
import {orderState} from "./state/orderState";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Order, OrderItem, OrderStatusRes} from "./lib/order_model";
import * as remote from "./lib/remote";
import {isShopOpenNow, closedReason, pickDayHours, formatTime12} from "./lib/hours";
import {SubmitIndicator} from "./component/loading";
import { Ionicons } from '@expo/vector-icons'; // Assuming you are using Ionicons
import safeStorage from "./lib/storage";
import { PALETTE } from "./lib/palette";

function Title({navigation}) {
    return (
        <HStack style={styles.header}>
            <BackButton
                onPress={() => navigation.pop()}
                containerStyle={CIRCLE_BTN}
                size={12}
                thickness={2.5}
                hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
            />
            <Text style={styles.headerTitle}>Order Details</Text>
            <Box style={styles.headerBack}/>
        </HStack>
    );
}

function Table() {
    const yourOrder = useRecoilValue(orderState);
    const accent = apps.getAccent();

    return (
        <Box style={styles.tableContainer}>
            <Text style={[styles.tableText, {color: accent}]}>{yourOrder.getOrderInfoLong()}</Text>
            <Image source={require("../assets/paperbottom.png")} alt="pb" h="18px" w="100%"/>
        </Box>
    );
}

function Header({shop}: { shop: ShopTblData }) {
    return (
        <VStack style={styles.container}>
            <Box style={styles.headerTop}>
                <Text style={styles.shopName}>{shop.name}</Text>
                {/* 주소 뒤에 state(NSW/QLD…) 부기 — Store Info와 동일. 서버 미배포 시 state undefined → 주소만. */}
                <Text style={styles.showAddress}>{[shop.address, shop.state].filter(Boolean).join(", ")}</Text>
            </Box>
            {/*<HStack style={styles.headerBottom}>
        <Text style={styles.pickupTimeLabel}>Pickup time</Text>
        <Text style={styles.pickupTime}>5-15 min</Text>
      </HStack>*/}
        </VStack>
    );
}

/**
 * Checkout 화면의 금액 계산을 한 곳에 모은다. Total 행과 Place Order 버튼이 각자 계산하던 탓에
 * 버튼만 공휴일 서차지를 빠뜨려 두 숫자가 어긋나 있었다 — 손님이 마지막으로 보는 두 금액이라
 * 반드시 같아야 한다.
 */
function useCheckoutTotals() {
    const cart = useRecoilValue(cartState);
    const yourOrder = useRecoilValue(orderState);
    const isCreditCard = yourOrder.payMethod === PayMethod.CREDIT_CARD;
    const shop = apps.currentShop;

    const subTotal = cart.total();

    // 공휴일 추가요금 — 결제수단과 무관하게 부과·표시한다(카운터결제도 카운터에서 청구됨).
    // 계산식은 tabletious Tx.calc() 복제(utils.holidaySurchargeAmount 주석 참조).
    const holidayPct = shop.holidaySurcharge ?? 0;
    const holidayAmt = holidaySurchargeAmount(subTotal, holidayPct);
    const payable = payableAmount(subTotal, holidayPct);   // 게이트웨이로 넘기는 금액

    //for domestic credit, debit, eftpos & Amex cards => 1.65% + $0.30
    //for international.... 2.75% + %0.30
    // 카드결제만 수수료 발생. surcharge(율)+fixed fee(정액)를 합산해 단일 "Processing Fee"로 표시.
    // 기준은 payable(소계+공휴일 서차지) — 게이트웨이가 넘긴 금액 전체에 수수료를 매긴다.
    // total은 합산 후 한 번만 반올림(줄별 반올림 합계 아님). surcharge·fixed 모두 0이면 행 숨김.
    const surchargeAmt = isCreditCard ? (payable * shop.cnpSurchargeRate / 100) : 0.0;
    const fixedFee = isCreditCard ? shop.cnpFixedTransactionFee : 0.0;
    const fee = surchargeAmt + fixedFee;

    return {
        subTotal, holidayAmt, payable, fee,
        total: payable + fee,
        showFee: fee > 0,
    };
}

function Total() {
    const shop = apps.currentShop;   // 수수료 안내 모달 문구에서 쓴다
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    const {subTotal, holidayAmt, fee, total, showFee} = useCheckoutTotals();

    // State to handle the modal visibility
    const [isModalVisible, setModalVisible] = useState(false);

    //console.log("yourOrder.payMethod: " + yourOrder.payMethod);

    return (
        <VStack style={styles.totalContainer}>
            <HStack style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalPrice}>{money(subTotal)}</Text>
            </HStack>

            {/* 공휴일 추가요금. 요율 0이면 숨김. 카드·카운터결제 모두 표시(결제 전 고지). */}
            {holidayAmt > 0 && (
                <HStack style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Holiday Surcharge</Text>
                    <Text style={styles.totalPrice}>{money(holidayAmt)}</Text>
                </HStack>
            )}

            {/* Processing Fee = CNP surcharge + fixed fee 합산. 둘 다 0이면 숨김. help ? 로 내역 안내. */}
            {showFee && (
                <HStack style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Processing Fee</Text>
                    <Pressable onPress={() => setModalVisible(true)} style={{ marginRight: 6 }}>
                        <Icon as={Ionicons} name="help-circle-outline" size={5} color={PALETTE.MUTED} />
                    </Pressable>
                    <Text style={styles.totalPrice}>{money(fee)}</Text>
                </HStack>
            )}

            {showFee && (
                <HStack style={{ flexDirection: 'row', justifyContent: 'flex-end', width: '100%' }}>
                    <Text style={{ color: PALETTE.DANGER }}>
                        * International Card will be different
                    </Text>
                </HStack>
            )}
            <HStack style={styles.totalRowLast}>
                <Text style={styles.totalLabelGrand}>Total</Text>
                <Text style={styles.totalPriceGrand}>{money(total)}</Text>
            </HStack>

            {/* Modal for Fee Explanation */}
            <Modal isOpen={isModalVisible} onClose={() => setModalVisible(false)}>
                <Modal.Content>
                    <Modal.Header>Processing Fee Calculation</Modal.Header>
                    <Modal.Body>
                        <Text>The processing fee is calculated as:</Text>
                        <Text>- {shop.cnpSurchargeRate}% (GST included) of the total amount</Text>
                        <Text>- A fixed transaction fee of {money(shop.cnpFixedTransactionFee)} (GST included) added to every transaction</Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bg={accent} _text={{ color: onAccent }} onPress={() => setModalVisible(false)}>Close</Button>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>

        </VStack>
    );
}

class PayMethods {
    methods: Map<PayMethod, Boolean> = new Map<PayMethod, Boolean>([
        [PayMethod.CREDIT_CARD, false],
        [PayMethod.PAYPAL, false],
        [PayMethod.STRIPE, false],
        [PayMethod.APPLE_PAY, false],
        [PayMethod.PAY_AT_COUNTER, false],
    ]);

    constructor() {
    }

    paymentOptionStyle(method: PayMethod) {
        return this.methods.get(method) ? styles.paymentOptionSelected : styles.paymentOption;
    }

    paymentOptionTextStyle(method: PayMethod) {
        return this.methods.get(method) ? styles.paymentOptionTextSelected : styles.paymentOptionText;
    }

    select(method: PayMethod) {
        if (this.methods.get(method)) return this;  // on인 상태에서는 터치를 해도 변하지 않도록 this를 리턴

        const newMethods = new PayMethods();
        newMethods.methods.set(method, true);
        return newMethods;
    }

    paymentOptionImage(method: PayMethod) {
        const isOn = this.methods.get(method);

        switch (method) {
            case PayMethod.CREDIT_CARD:
                return isOn ? require("../assets/pay-visa-on.png") : require("../assets/pay-visa-off.png");
            case PayMethod.PAYPAL:
                return isOn ? require("../assets/pay-paypal-on.png") : require("../assets/pay-paypal-off.png");
            case PayMethod.STRIPE:
                return isOn ? require("../assets/pay-stripe-on.png") : require("../assets/pay-stripe-off.png");
            case PayMethod.APPLE_PAY:
                return isOn ? require("../assets/pay-applepay-on.png") : require("../assets/pay-applepay-off.png");
            case PayMethod.PAY_AT_COUNTER:
                return isOn ? require("../assets/pay-payatcounter-on.png") : require("../assets/pay-payatcounter-off.png");
            default:
                return null;
        }
    }

    paymentOptionImageKey(method: PayMethod): number {
        return this.methods.get(method) ? 1 : 2;
    }

    paymentText(method: PayMethod): string {
        switch (method) {
            case PayMethod.CREDIT_CARD:
                return "Credit / Debit Card";
            case PayMethod.PAYPAL:
                return "PayPal";
            case PayMethod.STRIPE:
                return "Stripe";
            case PayMethod.APPLE_PAY:
                return "Apple Pay";
            case PayMethod.PAY_AT_COUNTER:
                return "Pay at Counter";
            default:
                return "Unknown";
        }
    }

    paymentSub(method: PayMethod): string {
        switch (method) {
            case PayMethod.CREDIT_CARD: {
                // surcharge 율 + fixed fee를 매장 설정에서 동적으로.
                //   rate 0.5 & fixed 0.30 → "Visa · Mastercard + (0.5% + $0.30) surcharge"
                //   rate 0.5 & fixed 0    → "Visa · Mastercard + (0.5%) surcharge"
                //   둘 다 0               → "Visa · Mastercard"
                const rate = apps.currentShop?.cnpSurchargeRate ?? 0;
                const fixed = apps.currentShop?.cnpFixedTransactionFee ?? 0;
                const base = "Visa · Mastercard";
                const parts: string[] = [];
                if (rate > 0) parts.push(`${rate}%`);
                if (fixed > 0) parts.push(money(fixed));
                return parts.length ? `${base} + (${parts.join(" + ")}) surcharge` : base;
            }
            case PayMethod.PAY_AT_COUNTER:
                return "Pay later at counter";
            default:
                return "";
        }
    }
}

function PayMethodOption({
                             method,
                             methods,
                             onSelect,
                         }: { method: PayMethod, methods: PayMethods, onSelect: (method: PayMethod) => void },
) {
    const accent = apps.getAccent();
    const accentTint = apps.accentTint();
    const isSelected = !!methods.methods.get(method);
    const sub = methods.paymentSub(method);
    return (
        <TouchableWithoutFeedback onPress={() => onSelect(method)}>
            <View style={[styles.pmTile, isSelected && { borderColor: accent, backgroundColor: accentTint }]}>
                <View style={[styles.pmRadioOuter, { borderColor: isSelected ? accent : PALETTE.BORDER }]}>
                    {isSelected && <View style={[styles.pmRadioInner, { backgroundColor: accent }]} />}
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text style={[styles.pmLabel, isSelected && { color: accent }]}>{methods.paymentText(method)}</Text>
                    {sub ? <Text style={styles.pmSub}>{sub}</Text> : null}
                </View>
            </View>
        </TouchableWithoutFeedback>
    );
}

function Payment() {
    const [order, setOrder] = useRecoilState(orderState);
    const [methods, setMethods] = useState(new PayMethods());

    const onSelect = (method: PayMethod) => {
        setMethods(methods.select(method));
        setOrder(order.updatePayMethod(method));
    };

    useEffect(() => {
        let method = PayMethod.PAY_AT_COUNTER;
        //console.log("default: " + JSON.stringify(apps.currentShop));   //${JSON.stringify(yourOrder)}
        if (apps.currentShop.defaultPaymentType == "CREDIT_CARD") {
            method = PayMethod.CREDIT_CARD;
            if (apps.currentShop.qrPaymentMethod == 0) method = PayMethod.PAY_AT_COUNTER;
        } else if (apps.currentShop.defaultPaymentType == "PAY_AT_COUNTER") {
            method = PayMethod.PAY_AT_COUNTER;
            if (apps.currentShop.qrPaymentMethod == 1) method = PayMethod.CREDIT_CARD;
        }
        setMethods(methods.select(method));
        setOrder(order.updatePayMethod(method));
    }, []);

    const accent = apps.getAccent();

    return (
        <VStack style={{marginTop: 55}}>
            <Box style={styles.paymentMethodBox}>
                <Text style={[styles.paymentMethodTitle, {color: accent}]}>Payment Method</Text>
            </Box>

            {/*<PayMethodOption method={PayMethod.PAYPAL} methods={methods} onSelect={onSelect} />
                <PayMethodOption method={PayMethod.CREDIT_CARD} methods={methods} onSelect={onSelect} />
                <PayMethodOption method={PayMethod.STRIPE} methods={methods} onSelect={onSelect} />
                <PayMethodOption method={PayMethod.APPLE_PAY} methods={methods} onSelect={onSelect} />

                // *******************************************
                // apps.currentShop.qrPaymentMethod ?
                // 0: pay at counter
                // 1: credit card
                // 9: All methods
                // *******************************************

            */}

            {(apps.currentShop.qrPaymentMethod == 1 || apps.currentShop.qrPaymentMethod == 9) && (
                <PayMethodOption method={PayMethod.CREDIT_CARD} methods={methods} onSelect={onSelect} />
            )}
            {(apps.currentShop.qrPaymentMethod == 0 || apps.currentShop.qrPaymentMethod == 9) && (
                <PayMethodOption method={PayMethod.PAY_AT_COUNTER} methods={methods} onSelect={onSelect}/>
            )}
        </VStack>
    );
}

const OrderButton: React.FC<{ onOrder: () => void, submitting?: boolean }> = ({onOrder, submitting}) => {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    // 바로 위 Total 행과 같은 계산을 쓴다 — 예전엔 cart.total()(소계)만 찍어 공휴일 서차지가 빠졌다.
    const {total} = useCheckoutTotals();

    return (
        <Box style={styles.addButtonPane}>
            <TouchableWithoutFeedback onPress={onOrder} disabled={submitting}>
                <HStack style={[styles.addButtonStack, {backgroundColor: accent}, submitting && styles.addButtonStackDisabled]}>
                    <Text style={[styles.addButtonLabel, {color: onAccent}]}>Place Order · </Text>
                    <Text style={[styles.addButtonPrice, {color: onAccent}]}>{money(total)}</Text>
                </HStack>
            </TouchableWithoutFeedback>
        </Box>
    );
};

const UserDetails: React.FC = () => {
    const [name, setName] = useState<string>(apps.name);
    const [mobile, setMobile] = useState<string>(apps.mobile);
    const accent = apps.getAccent();

    useEffect(() => {
        console.log(`name: ${name}, mobile: ${mobile}`);
        apps.name = name;
        apps.mobile = mobile;
    }, [name, mobile]);

    return (
        <VStack style={{marginTop: 55}}>
            <Box style={styles.paymentMethodBox}>
                <Text style={[styles.paymentMethodTitle, {color: accent}]}>Your Details</Text>
            </Box>
            <Text style={styles.inputLabel}>Your Name</Text>
            <TextInput style={styles.input} selectTextOnFocus={true}
                       value={name} onChangeText={newName => setName(newName)} maxLength={50}/>
            <Text style={styles.inputLabel}>Your Mobile Number</Text>
            <TextInput style={styles.input} selectTextOnFocus={true} keyboardType="numeric"
                       value={mobile} onChangeText={newMobile => setMobile(newMobile)} maxLength={20}/>
        </VStack>
    );
}

function ItemsAndBottom({onOrder, submitting}: { onOrder: () => void, submitting?: boolean }) {
    const cart = useRecoilValue(cartState);
    const {showHint, scrollToEnd, scrollProps} = useScrollMore();   // 스크롤 "더 있음" 힌트

    return (
        <VStack style={{...styles.container, flex: 1}}>
            <Box style={{
                height: 40,
                justifyContent: "center",
                borderBottomColor: PALETTE.TEXT,
                borderBottomWidth: 1,
            }}>
                <Text style={{
                    fontSize: 16,
                    fontFamily: "NotoSans-Black",
                    fontWeight: "900",
                    color: PALETTE.TEXT,
                }}>Your Order</Text>
            </Box>
            <FlatList {...scrollProps} data={mapToItems(cart.items)} scrollEnabled={true}
                      ListFooterComponent={
                          <>
                              <Total/>
                              {apps.isTakeaway() && <UserDetails/>}
                              <Payment />
                              <OrderButton onOrder={onOrder} submitting={submitting}/>
                          </>
                      }

                      renderItem={({item, index}) => {
                          const isLast = index === cart.items.length - 1;

                          return (
                              <VStack style={{width: "100%", backgroundColor: "white"}}>
                                  <HStack style={styles.itemList}>
                                      <ThumbImage shopid={apps.currentShop?.shopid ?? ""} menuid={item.menuid} imageVer={item.imageVer} style={styles.itemThumb} />
                                      <VStack style={styles.itemLeft}>
                                          <Text style={styles.itemName}>{item.name}</Text>
                                          <Text style={styles.itemOptions}>{item.optionsDescr()}</Text>
                                          {item.note ? <Text style={styles.itemNote}>📝 {item.note}</Text> : null}
                                      </VStack>
                                      <VStack style={styles.itemRight}>
                                          <Text style={styles.itemPrice}>{money(item.total)}</Text>
                                          <View style={styles.itemQtyChip}><Text style={styles.itemQtyChipText}>×{item.qty}</Text></View>
                                      </VStack>
                                  </HStack>
                                  {!isLast ?
                                      // 아래 Total 구분선(PALETTE.DIVIDER)과 톤을 맞춘 가는 점선
                                      <DashedLine dashLength={3} dashGap={3} dashThickness={1} dashColor={PALETTE.DIVIDER}
                                                  style={{height: 1}}/> : null
                                  }
                              </VStack>
                          );
                      }
                      }/>
            <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
        </VStack>
    );
}

// 모바일 키보드가 뜨면 visual viewport만 줄어든다(layout viewport=useWindowDimensions 높이는 그대로).
// 카드결제 iframe을 "보이는 영역"에 맞추기 위해 visualViewport 높이/오프셋을 실시간 추적.
// visualViewport 미지원 환경(네이티브 등)에서는 useWindowDimensions 높이로 폴백.
function useVisualViewport() {
    const { height: winHeight } = useWindowDimensions();
    const [vp, setVp] = useState({ height: winHeight, offsetTop: 0 });
    useEffect(() => {
        const vv = (typeof window !== "undefined") ? window.visualViewport : null;
        if (!vv) return;
        const update = () => setVp({ height: vv.height, offsetTop: vv.offsetTop });
        update();
        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
        return () => {
            vv.removeEventListener("resize", update);
            vv.removeEventListener("scroll", update);
        };
    }, []);
    const hasVV = (typeof window !== "undefined") && !!window.visualViewport;
    return hasVV ? vp : { height: winHeight, offsetTop: 0 };
}

function Checkout({navigation}) {
    const { width, height } = useWindowDimensions();
    // 카드결제 iframe = UrPay = Flutter Web. Flutter는 height:100% 표면에서 키보드 인셋을 스스로
    // 처리하므로, iframe을 "레이아웃 뷰포트 전체"(키보드 뒤까지)로 줘 Flutter가 키보드를 인식하고
    // 포커스된 필드(Expiry/Security)를 위로 스크롤하게 한다. + 헤더 제거로 세로 공간 최대 확보.
    // (visual viewport로 축소하면 iframe이 키보드 위에만 있어 Flutter가 키보드를 못 봄 → 아래 필드 안 올라옴.)
    const { offsetTop: vpOffsetTop } = useVisualViewport();
    const modalW = width;   // 풀스크린 가로
    const modalH = height;  // 풀스크린 세로(레이아웃 뷰포트 전체)
    const iframeW = modalW; // fills Modal.Body (p={0} removes default 24px padding)
    const iframeH = modalH; // 헤더 제거 → 전체를 iframe에
    const PAYMENT_TIMEOUT_MS = 10 * 60 * 1000;
    // Till 확정 폴링 데드라인. iframe 자체의 PAYMENT_TIMEOUT_MS(10분)와는 별개 시계 —
    // "Confirming your payment…" 스피너가 그만큼 오래 걸리게 두지 않기 위해 짧게 잡는다.
    const CONFIRM_TIMEOUT_MS = 60 * 1000;
    const happyhour: Happyhour = apps.getHappyhour();
    const saleMode: SaleMode = happyhour.saleMode;
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();



    const [cart, setCart] = useRecoilState(cartState);
    const yourOrder = useRecoilValue<YourOrder>(orderState);

    const [orderId, setOrderId] = useState<number>(0);  // order submit했을 때 받는 서버의 order.id
    const [submit, setSubmit] = useState(false);        // submit 진행중
    const [errorMessage, setErrorMessage] = useState("");        // submit 진행중
    const [errorVisible, setErrorVisible] = useState(false);        // submit 진행중
    const [cardPaymentModalVisible, setCardPaymentModalVisible] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState<string>("");     // State for payment URL
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [eventData, setEventData] = useState(null); // Store event data here
    const [paymentTimedOut, setPaymentTimedOut] = useState(false);
    const [waitingConfirm, setWaitingConfirm] = useState(false);   // Till: 반환페이지 신호 받고 서버 폴링 중
    const happyHourEndedRef = useRef(false);
    const placingRef = useRef(false);   // 중복 주문 가드: submit과 동기화되는 reentrancy 락
    const pollActiveRef = useRef(false);   // Till 폴링 진행 여부(중복 시작 방지 + 언마운트/완료 시 중단 신호)
    const pollTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);   // 언마운트 후 비동기 콜백이 setState/navigate하는 것 방지(Till 모달 닫기 1회성 조회용)
    // Till 폴링 시작 함수는 message 리스너 useEffect 안에서 정의된다. 결제 세션을 만드는 쪽(onPlaceOrder
    // 성공 핸들러)은 그 스코프 밖이라 직접 못 부르므로 ref로 노출한다.
    const startPollRef = useRef<((shopId: number, orderId: number, opts?: { background?: boolean }) => void) | null>(null);
    // Till 결제 세션을 이미 연 주문 id. orderStatus 폴링의 onSuccess가 응답마다 실행되기 때문에
    // 가드가 없으면 폴링 1회당 Till 거래가 1건씩 열린다(2026-09-19 로컬: 주문 하나에 세션 7건).
    const tillSessionOpenedForRef = useRef<number>(0);
    /** 지금 폴링 중인 주문 id. 주문이 바뀌면 이전 폴링을 끊고 새로 시작하기 위해 필요하다. */
    const pollOrderIdRef = useRef<number>(0);
    /**
     * 손님이 방금 Pay를 눌러 "주문 제출이 진행 중"인가. orderStatus 폴링의 onSuccess가 결제창을
     * 띄우거나 Ordered로 이동시키는 부작용을 갖고 있는데, 이 쿼리는 결제 대기 주문이 남아 있는 한
     * 계속 돈다 — 손님이 결제창을 닫고 메뉴를 더 담는 동안에도 실행되어, 장바구니에 담자마자
     * 결제도 없이 Ordered로 넘어가는 사고가 났다. 부작용은 이 플래그가 켜져 있을 때만 낸다.
     */
    const awaitingPlacementRef = useRef(false);

    // Inactivity Timeout 연동
    const setPaymentActive = useSetRecoilState(paymentActiveState);
    const lastCartTouch = useRecoilValue(lastCartTouchState);
    const [staleConfirmOpen, setStaleConfirmOpen] = useState(false);   // ① 제출 시점 stale 확인 다이얼로그
    const staleBypassRef = useRef(false);   // 확인 통과 후 재호출 시 stale 체크 우회

    // 카드결제 iframe이 열려있는 동안(+ Till 확정 폴링 중) IdleGuard를 상태 기반으로 정지
    // (cross-origin 활동 미감지 오발동 방지 — 폴링 중에도 손님은 화면을 보고만 있어 활동이 안 잡힌다)
    useEffect(() => {
        setPaymentActive(cardPaymentModalVisible || waitingConfirm);
    }, [cardPaymentModalVisible, waitingConfirm]);
    // 언마운트 시 결제 플래그 해제(누락 방지)
    useEffect(() => () => setPaymentActive(false), []);

    // 언마운트 여부 추적(mountedRef) — handleCloseModal의 Till 1회성 getPaymentStatus 조회가
    // 응답을 기다리는 사이 화면을 벗어나도(BackGuard Leave, idle reset 등) navigate/mutate/setState 안 하도록.
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // submit이 풀리는 모든 지점에서 ref도 자동 해제 (수동 리셋 누락 방지)
    useEffect(() => {
        placingRef.current = submit;
    }, [submit]);

    const placeOrder = useMutation((order: Order) => remote.placeOrder(order),
        {
            onSuccess: (res) => {
                //setOrderId(res.orderId);
                apps.ordId = res.orderId;
            },
            onError: (err) => {
                awaitingPlacementRef.current = false;
                // @ts-ignore
                //alert(`err: ${err}`);
                setErrorMessage(`err: ${err}`);
                setErrorVisible(true);
                setSubmit(false);   // POST 실패 시 락 해제 — 안 풀면 버튼 영구 비활성화되어 재시도 불가
            },
            onMutate: (order) => {
                //console.log("posting: ", JSON.stringify(order, null, 2));
                awaitingPlacementRef.current = true;
                setSubmit(true);
            },
        });

    // orderId가 0보다 크면 결과를 조회한다.
    // 키에 화면 이름과 주문 id를 넣는 이유 두 가지:
    //  1) ordered.tsx가 같은 `["orderStatus"]` 키를 2초마다 refetch한다. 키를 공유하면 그 결과가
    //     이 화면의 onSuccess까지 깨워, 손님이 아무것도 안 해도 결제 흐름이 다시 굴러간다.
    //  2) apps.ordId는 React 상태가 아니라 평범한 프로퍼티다. 키가 고정이면 새 주문을 만들어도
    //     같은 쿼리로 취급돼 요청이 안 나가고, 결제창이 영영 안 뜬 채 스피너만 돈다.
    const {isLoading, error, data} = useQuery<OrderStatusRes>(
        ["orderStatus", "checkout", apps.ordId],
        () => remote.getOrderStatus(apps.ordId),
        {
            enabled: apps.ordId > 0,
            retry: (failureCount, error) => failureCount < 7,
            retryDelay: 2000,
            onSuccess: (data: OrderStatusRes) => {

                if (data.orderStatus == "FAILED") {
                    awaitingPlacementRef.current = false;
                    //setOrderId(0);
                    apps.ordIdClear();
                    apps.ordNoClear();
                    setSubmit(false);
                    setCardPaymentModalVisible(false);
                    //alert(data.error);
                    setErrorMessage(data.error);
                    setErrorVisible(true);
                    return;
                }

                if (data.orderStatus == "CANCELED") {
                    awaitingPlacementRef.current = false;
                    //setOrderId(0);
                    apps.ordIdClear();
                    apps.ordNoClear();
                    setSubmit(false);
                    setCardPaymentModalVisible(false);
                    //alert(data.error);
                    return;
                }

                apps.ordNo = data.ordNo;
                apps.ordId = data.orderId;

                // 아래는 전부 "방금 제출한 주문"에 대한 처리다. 손님이 결제창을 닫고 메뉴를 더
                // 담는 동안에도 이 쿼리는 계속 돌기 때문에, 게이트 없이는 장바구니만 건드려도
                // 결제창이 다시 뜨거나 Ordered로 넘어간다.
                if (!awaitingPlacementRef.current) return;

                if (yourOrder.payMethod === PayMethod.CREDIT_CARD) {
                    if (apps.currentShop.cnpProvider === "TILL") {
                        // 이 onSuccess는 주문상태 폴링 응답마다 실행된다. UrPay 분기는 URL 문자열만
                        // 조립해서 반복돼도 무해하지만, createPaymentSession은 Till에 실제 거래를 여는
                        // POST다 — 가드가 없으면 폴링 1회당 거래가 1건씩 열리고, 결제가 끝난 뒤에도
                        // 계속 호출돼 "This order has already been paid" 오류가 반복해서 뜬다.
                        // 주문당 한 번만 연다(결제 실패 후 재시도는 새 주문 = 다른 id라 자연히 풀린다).
                        if (tillSessionOpenedForRef.current === data.orderId) return;
                        tillSessionOpenedForRef.current = data.orderId;
                        // Till은 API 키·서명이 필요해 서버만 거래를 열 수 있다. 금액은 서버가 orderId로 계산.
                        remote.createPaymentSession(apps.currentShop.id, data.orderId)
                            .then((s) => {
                                awaitingPlacementRef.current = false;
                                if (!s.redirectUrl) {
                                    setErrorMessage(s.error ?? "Payment could not be started.");
                                    setErrorVisible(true);
                                    setSubmit(false);
                                    return;
                                }
                                setPaymentUrl(s.redirectUrl);
                                setSubmit(false);
                                setCardPaymentModalVisible(true);
                                // 결제창을 띄우는 즉시 백그라운드 폴링 시작. Till은 iframe 안에서
                                // 우리 successUrl로 돌아오지 않으므로 이게 결제 완료를 알 수 있는
                                // 유일한 경로다(반환 신호가 오면 더 빨리 닫히는 보너스).
                                startPollRef.current?.(apps.currentShop.id, data.orderId, { background: true });
                            })
                            .catch((err) => {
                                // 네트워크 단절·타임아웃·5xx 등 transport 실패 — 안 풀면 submit 락 영구 고착
                                awaitingPlacementRef.current = false;
                                console.log(`createPaymentSession onError: err: ${err}`);
                                setErrorMessage("Payment could not be started. Please try again.");
                                setErrorVisible(true);
                                setSubmit(false);
                            });
                        return;
                    }
                    const paymentRefId = apps.currentShop.paymentRefId;
                    // 결제 금액 = 소계 + 공휴일 서차지. UrPay 가 여기에 카드수수료를 얹는다.
                    // 결제 금액은 반드시 2자리 소수 문자열로 전송("19.9"→UrPay가 floor로 19.89 처리하던 문제).
                    const payable = payableAmount(cart.total(), apps.currentShop.holidaySurcharge ?? 0);
                    const paymentUrl = 'https://pay.paymms.au/' + paymentRefId + '/' + data.orderId + '/' + payable.toFixed(2);
                    //console.log(paymentUrl);
                    setPaymentUrl(paymentUrl); // Set the payment URL state
                    setSubmit(false);
                    awaitingPlacementRef.current = false;
                    //setOrderId(0);
                    setCardPaymentModalVisible(true);
                    return;
                } else {
                    // Navigate to Ordered page if not using Credit Card
                    setSubmit(false);
                    awaitingPlacementRef.current = false;
                    navigation.navigate("Ordered");
                    return;
                }
            },
            onError: (err: Error) => {
                console.log(`onError: err: ${err}`);
                //setOrderId(0);
                apps.ordIdClear();
                apps.ordNoClear();
                setErrorMessage(err.message);
                setErrorVisible(true);
                setSubmit(false);
                return;
            }
        });

    // useEffect(() => {
    //     console.log(`Your Order: ${JSON.stringify(yourOrder)}`);
    // }, [yourOrder]);

    const onPlaceOrder = () => {
        //console.log("Order placed");
        //console.log(cart);
        //console.log(yourOrder);

        // 이미 제출 진행 중이면 무시 (같은 틱 연타 차단 — state는 비동기라 ref로 동기 차단)
        if (placingRef.current) return;

        // 카트에 메뉴 아이템이 하나도 없으면 주문 차단(백스톱). 카트 화면 버튼이 1차 게이트지만,
        // 무활동 리셋 등으로 체크아웃 중 카트가 비워지는 경로도 여기서 막는다. 금액이 아닌 아이템 수로 판정.
        if (cart.items.length === 0) {
            setErrorMessage("Your cart is empty. Please add items before placing your order.");
            setErrorVisible(true);
            return;
        }

        // 영업시간 재검증(하드 차단): 진입(splash)은 영업시간 게이트를 통과했지만
        // 그 뒤 영업종료·브레이크타임에 걸쳐 제출하는 경로를 여기서 막는다. splash와 동일한
        // isShopOpenNow/closedReason을, 진입 시 저장된 동일 shop 객체(apps.currentShop)에 재적용해 판정을 일치시킨다.
        const openShop = apps.currentShop;
        const nowTime = new Date();
        if (openShop && !isShopOpenNow(openShop, nowTime, openShop.holidays)) {
            // 브레이크타임(영업 윈도우 내 휴게)이면 종료가 아니라 재개 시각을 안내한다.
            const day = pickDayHours(openShop, nowTime.getDay());
            const reason = closedReason(day, nowTime.getHours() * 60 + nowTime.getMinutes());
            const msg = reason === "break"
                ? `We're on a break from ${formatTime12(day.breakStart)} to ${formatTime12(day.breakEnd)}. Ordering reopens at ${formatTime12(day.breakEnd)}.`
                : "Sorry, we're closed now. Please order during our opening hours.";
            setErrorMessage(msg);
            setErrorVisible(true);
            return;
        }

        if (yourOrder.payMethod == PayMethod.NOT_SELECTED) {
            // @ts-ignore
            //alert("Please select a payment method");
            setErrorMessage("Please select a payment method");
            setErrorVisible(true);
            return;
        }

        if (apps.isTakeaway() && (apps.name.length < 3 || apps.mobile.length < 10)) {
            //alert("Please enter your name(more than 2 characters) and mobile number");
            setErrorMessage("Please enter your name(more than 2 characters) and mobile number");
            setErrorVisible(true);
            return;
        }

        // ① 제출 시점 stale 확인: Inactivity Timeout 켜진 매장에서, 장바구니가 오래 전(무활동 기준의 2배)에
        //   담긴 채 제출되면(밖을 돌아다니다 실수로 제출 등) 한 번 확인받아 유령 오더를 방지한다.
        const shopCfg = apps.currentShop;
        if (shopCfg?.idleReset === true && !staleBypassRef.current && lastCartTouch > 0) {
            const staleMs = (shopCfg.idleConfirmMinutes ?? 10) * 60 * 1000;
            if (Date.now() - lastCartTouch > staleMs) {
                setStaleConfirmOpen(true);
                return;
            }
        }
        staleBypassRef.current = false;

        const order: Order = new Order(apps.currentShop?.id!!, apps.user.id, yourOrder.type,
            yourOrder.qrType, yourOrder.payMethod, yourOrder.tableNo, yourOrder.txId,
            cart.total(),
            cart.items.map(item => new OrderItem(item.menu.id, item.qty, tagOptions(item.options), item.itemPrice, item.saleMode, item.note)),
            apps.name, apps.mobile
        );

        safeStorage.setItem("name", apps.name);
        safeStorage.setItem("mobile", apps.mobile);

        // if (apps.ordId > 0) {
        //     //alert("order is already submitted and confirmed");
        //     //don't place order again if order already has been placed.
        //     //place order -> card payment -> failed -> click pay at counter to place order again.(duplicate)
        //     if (yourOrder.payMethod == PayMethod.CREDIT_CARD) {
        //         // @ts-ignore
        //         //alert("You have an unpaid order. Select 'Pay at Counter' and pay after your meal.");
        //         setCardPaymentModalVisible(true);
        //         return;
        //     } else if (yourOrder.payMethod == PayMethod.PAY_AT_COUNTER) {
        //         //navigation.navigate("Ordered");
        //         //place order -> card payment -> failed -> click pay at counter to place order again.(duplicate)
        //         //ignore the previous order and send it again with Pay At Counter paymethod.
        //         placeOrder.mutate(order);
        //         return;
        //     }
        // } else {
        //     placeOrder.mutate(order);
        // }

        // if (yourOrder.payMethod == PayMethod.CREDIT_CARD) {
        //     setCardPaymentModalVisible(true);
        // }
        placingRef.current = true;   // mutate 직전 동기 락 (onMutate의 setSubmit(true)보다 먼저 적용)
        placeOrder.mutate(order);
        return;


    };

    // if (error) {
    //     return (<Text>"An error has occurred: " + {error.message}</Text>);
    // }

    const handleCardPaymentSuccess = (receiptData) => {
        //setCardPaymentModalVisible(false);
        //console.log("orderId: ", apps.ordId);

        // Create an instance of the Receipt class
        //const receipt = new Receipt(receiptData["r"], receiptData["postmessage"], receiptData["card_category"], receiptData["card_expiry"], receiptData["card_holder"], receiptData["card_number"], receiptData["card_subcategory"], receiptData["card_type"], receiptData["iframe"], receiptData["show_email"], receiptData["show_extras"], receiptData["token"], receiptData["tokenize_only"], receiptData["v"], receiptData["sca_enabled"], receiptData["merchant"], receiptData["successful"], amount, receiptData["currency"], receiptData["message"], receiptData["reference"], receiptData["id"], receiptData["msg"], receiptData.metadata.original_amount, txTime, apps.currentShop.id, apps.ordId);
        const receipt = new Receipt(
            receiptData["authorization"],
            receiptData["id"],
            receiptData["card_number"],
            receiptData["card_holder"],
            receiptData["card_expiry"],
            receiptData["card_token"],
            receiptData["card_type"],
            receiptData["card_category"],
            receiptData["card_subcategory"],
            receiptData["amount"],
            receiptData["decimal_amount"],
            receiptData["successful"],
            receiptData["message"],
            receiptData["reference"],
            receiptData["currency"],
            receiptData["transaction_id"],
            receiptData["settlement_date"],
            receiptData["transaction_date"],
            receiptData["response_code"],
            receiptData["captured"],
            receiptData["captured_amount"],
            receiptData["rrn"],
            receiptData["cvv_match"],
            receiptData.metadata["original_amount"],
            receiptData.metadata["surcharge_amount"],
            receiptData.metadata["total"],
            receiptData.metadata["surcharge_amount_description"],
            receiptData.metadata["authorization_tracking_id"],  // optional field
            receiptData.metadata["card_sequence_number"],       // optional field
            receiptData.metadata["sca_exemption"],              // optional field
            receiptData.metadata["least_cost_routed"],          // optional field
            receiptData.metadata["original_transaction_reference"], // optional field
            receiptData["serviceId"],                 // optional field
            receiptData["trxSrc"],                    // optional field
            receiptData["success"],
            receiptData["meaning"],
            receiptData["next_steps"],                  // optional field
            apps.currentShop.id,                // shopId
            apps.ordId                         // orderId

        );

        // Log the receipt to the console to see its details
        //console.log(JSON.stringify(receipt, null, 2));

        saveReceiptData.mutate(receipt); // Save the data to the database
        //navigation.navigate("Ordered");

    };

    // Function to save the data to the database
    const saveReceiptData = useMutation((receipt: Receipt) => remote.saveReceipt(receipt),
        {
            onSuccess: (res) => {
                //setOrderId(res.orderId);
                console.log("receipt successfully saved: ", res.receiptId);
            },
            onError: (err) => {
                // @ts-ignore
                //alert(`err: ${err}`);
                setErrorMessage(`err: ${err}`);
                setErrorVisible(true);
            },
            onMutate: (receipt) => {
                //console.log("posting: ", JSON.stringify(receipt, null, 2));

            },
        });

    // Function to cancel the order
    const cancelOrder = useMutation((orderId: number) => remote.cancelOrder(orderId),
        {
            onSuccess: (res) => {
                //setOrderId(res.orderId);
                setCardPaymentModalVisible(false);
                apps.ordIdClear();
                apps.ordNoClear();
                console.log("order cancelled: ", res);
            },
            onError: (err) => {
                // @ts-ignore
                //alert(`err: ${err}`);
                apps.ordIdClear();
                apps.ordNoClear();
                setErrorMessage(`err: ${err}`);
                setErrorVisible(true);

            },
            onMutate: (receipt) => {
                //console.log("posting: ", JSON.stringify(receipt, null, 2));

            },
        });

    const handleCloseModal = () => {
        setCardPaymentModalVisible(false);

        if (apps.currentShop?.cnpProvider === "TILL") {
            // Till 경로에서는 eventData가 절대 채워지지 않는다(그 값을 채우는 건 UrPay의 성공 postMessage
            // 핸들러뿐이고, Till의 postMessage는 위조 가능해 애초에 신뢰하지 않기로 했다 — Task 13).
            // 그래서 "결제됐는지"를 판단할 유일하게 믿을 수 있는 방법은 서버 상태를 직접 물어보는 것뿐이다.
            // 이걸 안 하고 무조건 취소하면: 손님이 결제를 마치고 반환페이지 신호가 오기 전에(또는 대신) X를
            // 누른 경우 "카드는 청구됐는데 주문은 취소됨"이 된다 — 이 기능 전체에서 가장 나쁜 결말.
            const shopId = apps.currentShop.id;
            const orderId = apps.ordId;
            remote.getPaymentStatus(shopId, orderId)
                .then((s) => {
                    // tick()이 await 뒤에 pollActiveRef를 다시 확인하는 것과 같은 이유 — 이 응답을
                    // 기다리는 사이 화면을 벗어났으면(BackGuard Leave, idle reset 등) 아무것도 하지 않는다.
                    if (!mountedRef.current) return;
                    if (s.status === "PAID") {
                        setPaymentSuccess(true);
                        navigation.navigate("Ordered");
                        return;
                    }
                    if (s.status === "FAILED" || s.status === "NONE") {
                        cancelOrder.mutate(apps.ordId);
                        apps.ordIdClear();
                        apps.ordNoClear();
                        setErrorMessage("Your order has been cancelled. Please place your order again.");
                        setErrorVisible(true);
                        return;
                    }
                    if (s.status === "ABANDONED") {
                        // 손님이 이미 Till 결제창 안에서 스스로 취소했다 — 서버는 주문을 그대로 살려뒀으니
                        // (즉시 재결제 가능) FAILED/NONE처럼 주문을 취소하지 않는다. 모달은 위에서 이미 닫았고
                        // 손님도 자기가 취소한 걸 아니 경고 메시지 없이 조용히 체크아웃 화면으로 둔다.
                        return;
                    }
                    // PENDING: 결제가 진행 중이거나 콜백이 아직 도착하지 않았을 수 있다 — 주문을 건드리지 않는다.
                    // 서버 리컨실러/1시간 스윕이 정말로 버려진 주문을 정리한다. 손님에게는 중립적으로 안내.
                    setErrorMessage("We're still confirming your payment. Please check with staff before paying again.");
                    setErrorVisible(true);
                })
                .catch((err) => {
                    if (!mountedRef.current) return;
                    // 상태 조회 자체가 실패하면 판단 근거가 없다 — 이때도 취소하지 않는다.
                    // "취소 안 함"이 겁쟁이 선택처럼 보이겠지만 비대칭은 반대다: 취소 안 한 미결제 주문은
                    // 1시간 스윕이 조용히 정리하는 반면, 결제 완료된 주문을 취소하면 환불 처리 + 항의하는
                    // 손님이 남는다. 판단 불가 상태에서는 항상 "주문을 그대로 둔다" 쪽으로 기운다.
                    console.log(`getPaymentStatus (on Till modal close) error: ${err}`);
                    setErrorMessage("We're still confirming your payment. Please check with staff before paying again.");
                    setErrorVisible(true);
                });
            return;
        }

        if (eventData && eventData["success"] === true) {
            setPaymentSuccess(true);
            navigation.navigate("Ordered");
            return;
        } else {
            cancelOrder.mutate(apps.ordId);
            setCardPaymentModalVisible(false);
            apps.ordIdClear();
            apps.ordNoClear();
            //alert("Your order has been cancelled. Please place your order again.");
            setErrorMessage("Your order has been cancelled. Please place your order again.");
            setErrorVisible(true);
            //navigation.navigate("Ordered");
            return;
        }

    };

    useEffect(() => {

        let navigated = false; // Flag to check if navigation has already occurred

        // Till: 반환 페이지(우리 서버)가 보내는 postMessage는 내용이 없는 신호일 뿐이다.
        // 손님이 /api/payment/return/success를 브라우저로 직접 열어도 똑같은 메시지가 나갈 수 있으므로
        // event.data.outcome/status는 절대 결과로 취급하지 않는다 — "iframe을 닫고 서버에 물어보라"는
        // 신호로만 쓰고, 실제 결과는 오직 서명검증된 콜백을 반영하는 getPaymentStatus 폴링으로만 확정한다.
        // background=true면 결제창을 띄운 직후부터 조용히 도는 모드다. Till은 iframe 안에서 우리
        // successUrl로 리다이렉트하지 않아(2026-09-19 확인: 앱에서 결제 3건 성공, 반환 페이지 호출 0건)
        // 반환 신호를 기다리면 영영 안 온다. 확정 근거는 원래부터 서명검증된 콜백 + 이 폴링이므로,
        // 반환 신호 없이도 PAID를 직접 감지한다. 반환 신호가 오면 더 빨리 닫히는 보너스일 뿐이다.
        const pollPaymentStatus = (shopId: number, orderId: number, opts?: { background?: boolean }) => {
            // 같은 주문에 대한 중복 트리거는 무시한다.
            if (pollActiveRef.current && pollOrderIdRef.current === orderId) return;
            // 다른 주문이면 이전 폴링을 반드시 끊고 새로 시작한다. 손님이 결제창을 X로 닫고 메뉴를
            // 더 담아 다시 결제하면 새 주문이 만들어지는데, 예전 주문의 폴링이 살아 있으면
            // "이미 폴링 중"으로 판단해 새 주문은 폴링이 시작조차 안 됐다 — 결제해도 화면이
            // 안 넘어가고, 죽은 주문만 계속 조회하게 된다.
            if (pollTimeoutIdRef.current) {
                clearTimeout(pollTimeoutIdRef.current);
                pollTimeoutIdRef.current = null;
            }
            pollActiveRef.current = true;
            pollOrderIdRef.current = orderId;
            const background = opts?.background === true;
            if (!background) {
                setCardPaymentModalVisible(false);
                setWaitingConfirm(true);
            }
            // 백그라운드 모드의 데드라인은 결제창 자체 제한시간이다. 60초로 잡으면 손님이 카드를
            // 입력하는 도중에 폴링이 끊긴다.
            const deadlineMs = background ? PAYMENT_TIMEOUT_MS : CONFIRM_TIMEOUT_MS;

            const startedAt = Date.now();
            // 이 tick이 아직 "현재 주문"의 것인지. 폴링 대상이 바뀌면(손님이 취소하고 재주문) 예전
            // tick의 응답이 뒤늦게 돌아와 새 주문 화면을 엉뚱하게 움직이는 것을 막는다.
            const isCurrent = () => pollActiveRef.current && pollOrderIdRef.current === orderId;
            const tick = async () => {
                if (!isCurrent()) return; // 그 사이 언마운트/취소/다른 주문으로 교체됨
                try {
                    const s = await remote.getPaymentStatus(shopId, orderId);
                    if (!isCurrent()) return;
                    if (s.status === "PAID") {
                        pollActiveRef.current = false;
                        setWaitingConfirm(false);
                        // isOpen=false만으로는 부족하다 — 결제 모달이 트리에 남아 있으면 NativeBase
                        // 오버레이가 Ordered 화면 위에 회색 배경으로 살아남는다(2026-09-19 로컬 재현:
                        // 회색만 보이고 닫기 버튼을 눌러야 결제완료 화면이 드러남). 이 파일의 다른
                        // 성공 경로(874·911·1019)와 동일하게 모달 자체를 언마운트하고 이동한다.
                        setPaymentSuccess(true);
                        navigation.navigate("Ordered");
                        return;
                    }
                    if (s.status === "FAILED") {
                        pollActiveRef.current = false;
                        setWaitingConfirm(false);
                        setCardPaymentModalVisible(false);   // 백그라운드 모드에선 결제창이 아직 떠 있다
                        setErrorMessage("Payment failed. Please try again or try using a different card.");
                        setErrorVisible(true);
                        return;
                    }
                    if (s.status === "ABANDONED") {
                        // 손님이 Till 결제창을 스스로 취소/이탈했다는 게 서버에서 확정된 상태 — FAILED와
                        // 달리 "무슨 일이 있었는지" 손님이 이미 알고 있으므로 경고성 메시지를 띄우지 않는다.
                        // 그냥 폴링만 멈추고 체크아웃 화면으로 조용히 돌려보내 바로 재결제하게 한다.
                        pollActiveRef.current = false;
                        setWaitingConfirm(false);
                        setCardPaymentModalVisible(false);   // 백그라운드 모드에선 결제창이 아직 떠 있다
                        return;
                    }
                    // PENDING/NONE: 아직 콜백 미도착(NONE은 세션 행 생성 경합일 수 있음) — 데드라인까지 계속 폴링
                } catch (err) {
                    // 네트워크 순단 등 일시 실패 — Task 12와 같은 이유로 여기서 멈추면 스피너가 영구 고착된다.
                    // 데드라인 전까지는 실패를 무시하고 계속 폴링.
                    console.log(`getPaymentStatus poll error: ${err}`);
                }
                if (!isCurrent()) return;
                if (Date.now() - startedAt > deadlineMs) {
                    pollActiveRef.current = false;
                    setWaitingConfirm(false);
                    // 백그라운드 모드는 손님이 결제창을 그냥 열어둔 채 방치한 경우가 대부분이다.
                    // 여기서 경고를 띄우면 아직 결제도 시작 안 한 손님을 놀라게 한다 — 조용히 멈추고,
                    // 창을 닫을 때 도는 1회성 상태조회(handleCloseModal)가 뒤늦은 승인을 회수한다.
                    if (!background) {
                        setErrorMessage("We could not confirm your payment. Please check with staff before paying again.");
                        setErrorVisible(true);
                    }
                    return;
                }
                pollTimeoutIdRef.current = setTimeout(tick, 2000);
            };
            tick();
        };
        // 결제 세션을 만드는 쪽에서 부를 수 있게 노출한다.
        startPollRef.current = pollPaymentStatus;

        const messageHandler = (event: MessageEvent) => {
            if (apps.currentShop.cnpProvider === "TILL") {
                // Till 반환 페이지는 우리 서버 자체 오리진에서 뜬다(Till의 오리진이 아님) — apps.profile 기준으로 매 호출 계산.
                // ⚠ Till은 iframe 안에서 이 페이지로 리다이렉트하지 않는 것으로 확인됐다(2026-09-19). 즉 이
                //   핸들러는 실제로는 거의 타지 않는다 — 결제 확정은 결제창을 열 때 시작한 백그라운드 폴링이
                //   담당한다. 여기는 "신호가 오면 더 빨리 닫는" 보조 경로로만 남긴다.
                if (event.origin !== remote.getApiOrigin()) return;
                if (event.data?.source !== "till") return;
                pollPaymentStatus(apps.currentShop.id, apps.ordId);
                return;
            }

            if (event.origin !== "https://pay.paymms.au") {
                return;
            }

            if (event.data["success"] != undefined) {
                if (event.data["success"] === true) {
                    handleCardPaymentSuccess(event.data.result);
                    setEventData(event.data.result); // Save event data
                } else {
                    // Handle payment failure
                   // alert("Payment failed. Please try again or close this window to complete your payment at the counter.");
                    setErrorMessage("Payment failed. Please try again or try using a different card.");
                    setErrorVisible(true);
                }
            }

            //여기에 모든 로그를 남기도록 하자.

            //Next 버튼을 눌렀을때, 현재 iframe 을 없애고, Ordered 페이지로 이동한다.
            if (event.data["action"] == "success" && !navigated) {
                navigated = true; // Set the flag to true after navigation
                setCardPaymentModalVisible(false);
                setPaymentSuccess(true);
                navigation.navigate("Ordered");
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
            // 언마운트(예: 폴링 중 화면 이탈) 시 폴링 중단 — 죽은 컴포넌트에서 navigate 호출 방지
            pollActiveRef.current = false;
            if (pollTimeoutIdRef.current) {
                clearTimeout(pollTimeoutIdRef.current);
                pollTimeoutIdRef.current = null;
            }
        };
    }, []);

    // 결제 모달이 열릴 때만 타임아웃 시작 (모달 닫히면 자동 취소)
    useEffect(() => {
        if (!cardPaymentModalVisible) {
            setPaymentTimedOut(false); // 모달 닫힐 때 플래그 리셋
            return;
        }
        const timeoutId = setTimeout(() => {
            setPaymentTimedOut(true);
        }, PAYMENT_TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
    }, [cardPaymentModalVisible]); // paymentSuccess 의존성 제거 — 타이머 재시작 방지

    // saleMode를 deps에 포함해서 happy hour가 체크아웃 화면에 있는 동안 만료될 때도 감지
    // happyHourEndedRef 가드로 알림은 정확히 1회만 표시
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if ((apps.saleMode == SaleMode.HAPPY1 || apps.saleMode == SaleMode.HAPPY2) &&
            saleMode == SaleMode.NORMAL &&
            !happyHourEndedRef.current) {
            happyHourEndedRef.current = true;
            const descr = apps.saleMode == SaleMode.HAPPY1
                ? apps.currentShop.happy1Descr
                : apps.currentShop.happy2Descr;
            setErrorMessage(`${descr} has ended. Please reorder from the beginning`);
            setErrorVisible(true);
        }
    }, [saleMode]);


    return (
        <VStack style={{flex: 1, width: "100%", backgroundColor: "white"}}>
            <Title navigation={navigation}/>
            <Table/>
            <Header shop={apps.currentShop!!}/>
            <ItemsAndBottom onOrder={onPlaceOrder} submitting={submit}/>
            {submit && <SubmitIndicator/>}
            {!paymentSuccess && (
            <Modal isOpen={cardPaymentModalVisible} onClose={handleCloseModal} closeOnOverlayClick={false} justifyContent="flex-start">
                <Modal.Content mt={vpOffsetTop} width={modalW} height={modalH} maxWidth={modalW} maxHeight={modalH}>
                    <Modal.CloseButton />
                    {/* 헤더 제거: Flutter 폼에 세로 공간 최대 확보(닫기는 우상단 CloseButton 유지) */}
                    <Modal.Body p={0}>
                        {paymentTimedOut && (
                            <Text style={{ color: PALETTE.DANGER, marginBottom: 8, textAlign: 'center' }}>
                                {"Payment confirmation is taking longer than expected.\n"}
                                {"If payment was completed, please close this window and proceed.\n"}
                                {"If not, please close and try again."}
                            </Text>
                        )}
                        {/* allow="payment": Google Pay(및 Safari의 Apple Pay)는 Payment Request API를
                            쓰는데, 교차 출처 iframe에서는 부모가 이 권한을 위임하지 않으면 브라우저가
                            차단해 버튼이 조용히 안 뜬다. 위임 범위는 iframe src 오리진(결제 게이트웨이)뿐. */}
                        <iframe
                            id="paymentIframe"
                            src={paymentUrl}
                            title="Card Payment"
                            allow="payment"
                            style={{ width: iframeW, height: paymentTimedOut ? iframeH - 60 : iframeH, borderWidth: 0 }}
                        />
                    </Modal.Body>
                </Modal.Content>
            </Modal>
            )}
            {/* Till: 반환페이지 신호를 받은 뒤 서버 콜백 확정을 폴링하는 동안 표시. 결과가 나올 때까지 닫을 수 없다
                (증명 없는 결제 화면으로 손님이 빠져나가 다시 Pay를 눌러 중복 주문하는 것을 막기 위함).
                위 결제 모달과 같은 !paymentSuccess 가드를 둔다 — isOpen=false로만 닫고 곧바로 화면을
                이동하면 NativeBase 오버레이가 포털에 남아 Ordered 화면을 회색으로 덮는다. */}
            {!paymentSuccess && (
            <Modal isOpen={waitingConfirm} onClose={() => {}} closeOnOverlayClick={false}>
                <Modal.Content>
                    <Modal.Body>
                        <VStack space={3} alignItems="center" py={6}>
                            <Spinner size="lg" />
                            <Text>Confirming your payment…</Text>
                        </VStack>
                    </Modal.Body>
                </Modal.Content>
            </Modal>
            )}
            <Modal isOpen={errorVisible} onClose={() => setErrorVisible(false)}>
                <Modal.Content>
                    <Modal.Header>{`${apps.currentShop!!.name} Says`}</Modal.Header>
                    <Modal.Body>
                        <Text>{errorMessage}</Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button style={{width: "100px"}} bg={accent} _text={{ color: onAccent }} onPress={() => {
                            setErrorVisible(false);
                            if (happyHourEndedRef.current) {
                                // popToTop으로 Checkout을 스택에서 제거하고 Shop으로 복귀
                                navigation.popToTop();
                            }
                        }}> OK </Button>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>

            {/* ① 제출 시점 stale 확인: 오래된 장바구니를 실수로 제출하는 유령 오더 방지 */}
            <Modal isOpen={staleConfirmOpen} onClose={() => setStaleConfirmOpen(false)}>
                <Modal.Content>
                    <Modal.Header>Still want to order?</Modal.Header>
                    <Modal.Body>
                        <Text>You started this order more than {apps.currentShop?.idleConfirmMinutes ?? 10} minutes ago. Do you still want to place it?</Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={3}>
                            <Button variant="outline" borderColor={accent} _text={{ color: accent }}
                                onPress={() => setStaleConfirmOpen(false)}>Keep reviewing</Button>
                            <Button bg={accent} _text={{ color: onAccent }} onPress={() => {
                                setStaleConfirmOpen(false);
                                staleBypassRef.current = true;   // 다음 호출은 stale 체크 우회
                                onPlaceOrder();
                            }}>Place order</Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
        </VStack>
    );
}

const styles = StyleSheet.create({
    container: {
        marginTop: 15,
        marginLeft: 20,
        marginRight: 20,
    }, headerTop: {
        borderBottomColor: PALETTE.TEXT,
        borderBottomWidth: 2,
    }, shopName: {
        fontSize: 16,
        fontFamily: "NotoSans-Black",
        fontWeight: "900",
        color: PALETTE.TEXT,
        marginBottom: 4,
        letterSpacing: -0.4,
    }, showAddress: {
        fontSize: 12,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.TEXT,
        marginBottom: 9,
        letterSpacing: -0.3,
    }, headerBottom: {
        borderBottomColor: PALETTE.TEXT,
        borderBottomWidth: 1,
        height: 54,
        alignItems: "flex-end",
        paddingBottom: 8,
    }, pickupTime: {
        fontSize: 13,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.TEXT,
        letterSpacing: -0.33,
    }, pickupTimeLabel: {
        fontSize: 13,
        fontFamily: "NotoSans-Bold",
        fontWeight: "bold",
        color: PALETTE.TEXT,
        letterSpacing: -0.33,
        flex: 1,
    }, itemList: {
        width: "100%", backgroundColor: "white", minHeight: 72,
        // borderBottomWidth: 1, borderBottomColor: PALETTE.TEXT,
        paddingTop: 14, paddingBottom: 8, alignItems: "center",   // 상단 패딩 확대로 위 구분선과 간격 확보(하단은 수량 칩이 아래 구분선에 닿지 않게). minHeight: note 있으면 늘어남.
    }, itemThumb: {
        width: 40, height: 40, borderRadius: PALETTE.RADIUS_SM, marginRight: 10, backgroundColor: PALETTE.BORDER,
    }, itemLeft: {
        flex: 1, justifyContent: "center",
    }, itemName: {
        fontSize: 14,
        fontFamily: "NotoSans-Bold",
        fontWeight: "bold",
        letterSpacing: -0.3,
        color: PALETTE.TEXT,
    }, itemOptions: {
        fontSize: 12,
        fontFamily: "NotoSans-Regular",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
    }, itemNote: {
        fontSize: 11,
        fontFamily: "NotoSans-Regular",
        fontStyle: "italic",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
        marginTop: 2,
    }, itemRight: {
        justifyContent: "flex-start", alignItems: "flex-end",
    }, itemPrice: {
        fontSize: 16,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    }, itemQtyChip: {
        marginTop: 4,
        borderRadius: PALETTE.RADIUS_PILL,
        paddingHorizontal: 8,
        paddingVertical: 1,
        backgroundColor: PALETTE.QTY_CHIP,
    }, itemQtyChipText: {
        fontSize: 11,
        fontFamily: "NotoSans-Bold",
        fontWeight: "700",
        color: "#FFFFFF",
        letterSpacing: -0.2,
    }, addButtonPane: {
        width: "100%",
        height: 150,
    }, addButtonStack: {
        backgroundColor: PALETTE.ACCENT,
        borderRadius: PALETTE.RADIUS_MD,
        margin: 20,
        height: 58,
        marginLeft: 0,
        marginRight: 0,
        alignItems: "center",
        padding: 20,
    }, addButtonStackDisabled: {
        opacity: 0.5,
    }, addButtonLabel: {
        flex: 1, fontSize: 18, fontFamily: "NotoSans-Bold", fontWeight: "bold", color: "white",
    }, addButtonPrice: {
        fontSize: 18, fontFamily: "NotoSans-Bold", fontWeight: "bold", color: "white",
    }, totalContainer: {
        backgroundColor: PALETTE.SURFACE,
        paddingTop: 12,
        paddingBottom: 4,
    }, totalRow: {
        width: "100%",
        alignItems: "center",
        paddingVertical: 6,
        marginBottom: 2,
        borderTopWidth: 1,
        borderTopColor: PALETTE.DIVIDER,   // Subtotal 위 실선(Total 위 라인과 동일 기준)
    }, totalLabel: {
        flex: 1,
        fontSize: 13,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.MUTED,
    }, totalPrice: {
        fontSize: 13,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.TEXT,
        fontWeight: "600",
    }, totalRowLast: {
        width: "100%",
        alignItems: "center",
        paddingTop: 10,
        paddingBottom: 6,
        marginTop: 6,
        borderTopWidth: 1,
        borderTopColor: PALETTE.DIVIDER,
    }, totalLabelGrand: {
        flex: 1,
        fontSize: 17,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        color: PALETTE.TEXT,
        letterSpacing: -0.4,
    }, totalPriceGrand: {
        fontSize: 17,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        color: PALETTE.PRICE,
        letterSpacing: -0.4,
    }, titleContainer: {
        height: 49,
        backgroundColor: "white",
        justifyContent: "flex-start",
        alignItems: "center",
        marginLeft: 20,
    }, title: {
        fontSize: 22,
        fontFamily: "NotoSans-Black",
        fontWeight: "900",
        color: PALETTE.TEXT,
        letterSpacing: -0.55,
        lineHeight: 35,
    // 통일 헤더(OrderReview/ordered와 동일): back | 가운데 타이틀 | 스페이서
    }, header: {
        width: "100%",
        height: 56, alignItems: "center", justifyContent: "space-between",
        paddingHorizontal: 14, backgroundColor: "#fff",
        borderBottomWidth: 1, borderBottomColor: PALETTE.BORDER,
    }, headerBack: {
        width: 26, height: 26,
    }, headerTitle: {
        fontSize: 18, fontFamily: "NotoSans-Black", fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.4,
    }, paymentOption: {
        height: 58, borderColor: PALETTE.BORDER, borderWidth: 1,
        borderRadius: 20, marginTop: 10,
        alignItems: "center", justifyContent: "space-between",
        paddingLeft: 20, paddingRight: 0,
    }, paymentOptionSelected: {
        height: 58, borderColor: PALETTE.ACCENT, borderWidth: 1,
        borderRadius: 20, marginTop: 10,
        alignItems: "center", justifyContent: "space-between",
        paddingLeft: 20, paddingRight: 0,
    }, paymentOptionText: {
        color: PALETTE.MUTED, fontSize: 16, fontFamily: "NotoSans-Bold", fontWeight: "bold", letterSpacing: -0.4,
    }, paymentOptionTextSelected: {
        color: PALETTE.ACCENT, fontSize: 16, fontFamily: "NotoSans-Bold", fontWeight: "bold", letterSpacing: -0.4,
    }, paymentMethodBox: {
        height: 30, borderBottomColor: PALETTE.BORDER, borderBottomWidth: 1,
        marginBottom: 5,
    }, paymentMethodTitle: {
        color: PALETTE.TEXT, fontSize: 16, fontFamily: "NotoSans-Black", fontWeight: "900", letterSpacing: -0.4,
    }, tableContainer: {
        height: 71, width: "100%", backgroundColor: PALETTE.BG,
        borderTopWidth: 1, borderTopColor: PALETTE.BORDER, alignItems: "center",
        justifyContent: "flex-end",
    }, tableText: {
        fontSize: 16, fontFamily: "NotoSans-Black", letterSpacing: -0.4, color: PALETTE.ACCENT, fontWeight: "900",
    }, titleBack: {
        height: 26, width: 26,
        marginTop: 5, marginRight: 10,
    }, input: {
        height: 38, borderWidth: 0.5, padding: 10, fontSize: 14,
        fontFamily: "NotoSans-Regular", fontWeight: "500", letterSpacing: -0.4, color: PALETTE.MUTED, marginBottom: 10,
        borderTopColor: PALETTE.MUTED, borderLeftColor: PALETTE.MUTED, borderRightColor: PALETTE.MUTED, borderBottomColor: PALETTE.MUTED,
        borderRadius: 5, backgroundColor: PALETTE.SUBTLE,
    },
    inputLabel: {
        fontSize: 14, fontFamily: "NotoSans-Regular", fontWeight: "500", letterSpacing: -0.4, color: PALETTE.MUTED, padding: 5,
    }, pmTile: {
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1,
        borderColor: PALETTE.CARD_BORDER,
        borderRadius: 12,
        marginTop: 10,
        paddingVertical: 14,
        paddingHorizontal: 16,
        backgroundColor: PALETTE.SURFACE,
    }, pmRadioOuter: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: PALETTE.BORDER,
        alignItems: "center",
        justifyContent: "center",
    }, pmRadioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
    }, pmLabel: {
        fontSize: 15,
        fontFamily: "NotoSans-Regular",
        fontWeight: "600",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    }, pmSub: {
        fontSize: 12,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.MUTED,
        marginTop: 2,
        fontWeight: "500",
    },
});

export default Checkout;
